import { useEffect, useRef, useCallback } from "react";

interface UseIdleDetectionOptions {
  idleTime: number; // in milliseconds
  onIdle: () => void;
  events?: string[];
  enabled?: boolean;
}

export function useIdleDetection({
  idleTime,
  onIdle,
  events = [
    "mousedown",
    "mousemove",
    "keypress",
    "scroll",
    "touchstart",
    "click",
  ],
  enabled = true,
}: UseIdleDetectionOptions) {
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Check for development test mode
  const getEffectiveIdleTime = useCallback(() => {
    // Check URL parameters for testing
    const urlParams = new URLSearchParams(window.location.search);
    const testTimeout = urlParams.get("testIdleTimeout");

    if (testTimeout && process.env.NODE_ENV === "development") {
      const testTime = parseInt(testTimeout, 10);
      if (!isNaN(testTime) && testTime > 0) {
        return testTime;
      }
    }

    return idleTime;
  }, [idleTime]);

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    // Clear existing timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    // Update last activity time
    lastActivityRef.current = Date.now();

    // Set new timer with effective idle time
    const effectiveIdleTime = getEffectiveIdleTime();
    idleTimerRef.current = setTimeout(() => {
      // User idle timeout reached, triggering onIdle callback
      onIdle();
    }, effectiveIdleTime);
  }, [onIdle, enabled, getEffectiveIdleTime]);

  const handleUserActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) {
      // Clear timer if disabled
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Start the initial timer
    resetTimer();

    // Cleanup function
    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [events, handleUserActivity, resetTimer, enabled]);

  // Return functions to manually control the timer
  return {
    resetTimer,
    getLastActivity: () => lastActivityRef.current,
    getTimeUntilIdle: () => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      const effectiveIdleTime = getEffectiveIdleTime();
      return Math.max(0, effectiveIdleTime - timeSinceLastActivity);
    },
  };
}
